<template>
  <div class="happiness-id-card-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <div class="brand-badge mr-2">
          <feather-icon icon="ShieldIcon" size="20" class="text-white" />
        </div>
        <h4 class="mb-0 font-weight-bolder">
          Happiness Foundation
          <span class="text-primary">| ID Card Generator</span>
        </h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2 px-1">
          {{ reportItems.length }} Students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print All</span>
        </button>
        <button class="btn-modern btn-close-btn" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Exit</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5 state-wrapper">
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem"
        class="mb-3"
      />
      <h4 class="text-primary font-weight-bold">Loading student records…</h4>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5 state-wrapper">
      <feather-icon
        icon="AlertTriangleIcon"
        size="48"
        class="text-danger mb-2"
      />
      <h5 class="text-danger">{{ error }}</h5>
      <b-button variant="outline-primary" class="mt-2" @click="loadReportData"
        >Retry</b-button
      >
    </div>

    <!-- Cards Grid -->
    <div v-else class="cards-grid">
      <div
        v-for="(student, index) in reportItems"
        :key="index"
        class="card-pair"
      >
        <!-- ── FRONT ── -->
        <div class="id-card front">
          <!-- Curved header -->
          <div class="card-header-curve">
            <img
              src="https://cdn.cloudious.net/file-1777277212584-195054657.svg"
              alt="Logo"
              class="header-logo"
            />
            <p class="header-tagline">STUDENT ID CARD</p>
            <!-- Curved bottom edge -->
            <div class="curve-bottom"></div>
          </div>

          <!-- Circular photo -->
          <div class="photo-ring">
            <img
              :src="student.picture || student.photo || placeholder"
              alt="Photo"
              class="photo-img"
              @error="fixPhoto"
            />
          </div>

          <!-- Name & designation -->
          <div class="student-name">
            {{ (student.name || "N/A").toUpperCase() }}
          </div>

          <!-- Divider -->
          <div class="info-divider"></div>

          <!-- Info rows -->
          <div class="info-section">
            <div class="info-row">
              <feather-icon icon="HashIcon" size="11" class="info-icon" />
              <span class="info-label">ID No</span>
              <span class="info-value"
                >: {{ student.grNo || student.gr || "---" }}</span
              >
            </div>
            <div class="info-row">
              <feather-icon icon="UserIcon" size="11" class="info-icon" />
              <span class="info-label">Father</span>
              <span class="info-value"
                >:
                {{
                  (
                    student.father_name ||
                    student.fatherName ||
                    "---"
                  ).toUpperCase()
                }}</span
              >
            </div>
            <div class="info-row">
              <feather-icon icon="UsersIcon" size="11" class="info-icon" />
              <span class="info-label">Group</span>
              <span class="info-value"
                >:
                {{
                  (student.className || student.meta_class || "---").split(
                    " - "
                  )[0]
                }}</span
              >
            </div>
            <div class="info-row">
              <feather-icon icon="LayersIcon" size="11" class="info-icon" />
              <span class="info-label">Level</span>
              <span class="info-value"
                >:
                {{
                  (student.className || student.meta_class || "").split(
                    " - "
                  )[1] ||
                  student.sectionName ||
                  student.meta_section ||
                  "---"
                }}</span
              >
            </div>
            <div class="info-row" v-if="student.contact || student.phone">
              <feather-icon icon="PhoneIcon" size="11" class="info-icon" />
              <span class="info-label">Contact</span>
              <span class="info-value"
                >: {{ student.contact || student.phone }}</span
              >
            </div>
          </div>

          <!-- Footer -->
          <div class="card-footer-bar"></div>
        </div>

        <!-- ── BACK ── -->
        <div class="id-card back">
          <!-- Back full header -->
          <div class="back-header-bar">
            <img
              src="https://cdn.cloudious.net/file-1777277212584-195054657.svg"
              alt="Logo"
              class="back-logo"
            />
            <div class="back-curve-bottom"></div>
          </div>

          <!-- Terms -->
          <div class="terms-section">
            <p class="terms-title">Terms and Conditions:</p>
            <p class="terms-text">
              This card is the property of Happy Place and must be returned upon
              request. If found, please return to the address below.
            </p>
          </div>

          <div class="divider-dashed"></div>

          <!-- Valid dates single line -->
          <div class="dates-section-single">
            <span class="date-item"
              ><span class="date-label">Session:</span>
              <span class="date-val">{{ sessYear }}</span></span
            >
          </div>

          <div class="divider-dashed"></div>

          <!-- QR Center Section -->
          <div class="back-qr-container">
            <qrcode-vue :value="student.id.toString()" :size="95" level="H" />
          </div>

          <!-- Bottom Signature -->
          <div class="back-footer-sig">
            <div class="sig-line"></div>
            <span class="sig-label">Principal Signature</span>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-if="reportItems.length === 0"
        class="text-center py-5 state-wrapper"
        style="width: 100%"
      >
        <feather-icon icon="InboxIcon" size="56" class="text-muted" />
        <h4 class="mt-2">No students found</h4>
        <p class="text-muted">Adjust your filters and try again.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import QrcodeVue from "qrcode.vue";

export default {
  name: "happinessFoundationIDCard",
  components: { BButton, BSpinner, BBadge, QrcodeVue },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      db: "",
      cID: "",
      classID: 0,
      sectionID: 0,
      status: "present",
      sessYear: "",
      sessionID: 0,
      studentIDs: [],
      placeholder:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23e8ecf0'/%3E%3Ccircle cx='50' cy='38' r='20' fill='%23adb5bd'/%3E%3Cpath d='M10 100 Q10 68 50 68 Q90 68 90 100Z' fill='%23adb5bd'/%3E%3C/svg%3E",
    };
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const q = this.$route.query;
      this.db = q.db || this.$store.state.userData.db;
      this.cID = q.cID || 0;
      this.classID = q.cls || "";
      this.sectionID = q.sec || "";
      this.status = q.status || "present";
      this.sessYear =
        q.sess ||
        new Date().getFullYear() + "-" + (new Date().getFullYear() + 1);
      this.sessionID = q.sessionID || 0;
      if (q.studentIDs)
        this.studentIDs = q.studentIDs.split(",").map(Number).filter(Boolean);
      if (!this.db) {
        this.error = "Missing database context.";
        return;
      }
      this.loadReportData();
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;
      try {
        let url, params;
        if (this.classID && this.classID !== "0" && this.classID !== 0) {
          url = "https://apiv26.myskool.app/api/students/Filterdata";
          params = {
            cls: this.classID,
            sec: this.sectionID || 0,
            status: this.status,
            db: this.db,
            cId: this.cID,
            page: 1,
          };
        } else {
          url = "https://apiv26.myskool.app/api/Students/LoadDataPagination";
          params = {
            db: this.db,
            cID: this.cID,
            clsID: 0,
            section: 0,
            page: 1,
            status: this.status,
          };
        }
        const res = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });
        let all = [];
        if (res.data?.status === "success") {
          const d = res.data.data;
          all = Array.isArray(d) ? d : d?.data || [];
        } else if (Array.isArray(res.data)) {
          all = res.data;
        } else {
          this.error = res.data?.message || "No student data returned.";
        }
        this.reportItems = this.studentIDs.length
          ? all.filter((s) => this.studentIDs.includes(Number(s.id)))
          : all;
      } catch (e) {
        this.error = "Connection error. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    fixPhoto(e) {
      e.target.src = this.placeholder;
    },
    print() {
      window.print();
    },
    close() {
      this.$router.push({
        name: this.studentIDs.length ? "manage-studentgrid" : "report",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Outfit:wght@600;700;800;900&display=swap");

$navy: #032355;
$navy-mid: #0a3a7e;
$accent: #ea5455;
$light-bg: #f4f6fb;

* {
  box-sizing: border-box;
}

.happiness-id-card-report {
  background: $light-bg;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Inter", sans-serif;
}

/* ─── Control Bar ─── */
.control-bar {
  background: white;
  padding: 1rem 2rem;
  border-radius: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07);
  position: sticky;
  top: 1rem;
  z-index: 999;

  .brand-badge {
    background: $navy;
    padding: 8px;
    border-radius: 10px;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 0.6rem 1.4rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.25s ease;
  &:active {
    transform: scale(0.96);
  }
}
.btn-print {
  background: $navy;
  color: white;
  &:hover {
    background: $navy-mid;
  }
}
.btn-close-btn {
  background: #f1f3f5;
  color: #555;
  &:hover {
    background: #e2e6ea;
  }
}

/* ─── Grid ─── */
.cards-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto;
}

.card-pair {
  display: flex;
  gap: 14px;
  break-inside: avoid;
}

/* ─── Base Card ─── */
.id-card {
  width: 5.6cm;
  height: 9.2cm;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(3, 35, 85, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid rgba(3, 35, 85, 0.08);
}

/* ─── FRONT ─── */
.front {
  .card-header-curve {
    background: $navy;
    padding: 14px 12px 36px 12px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;

    .header-logo {
      height: 52px;
      object-fit: contain;
    }

    .header-tagline {
      color: rgba(255, 255, 255, 0.75);
      font-size: 7pt;
      font-weight: 600;
      letter-spacing: 1.5px;
      margin: 4px 0 0 0;
    }

    .curve-bottom {
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 28px;
      background: white;
      border-radius: 50% 50% 0 0 / 100% 100% 0 0;
    }
  }

  /* Circular photo */
  .photo-ring {
    width: 2.2cm;
    height: 2.2cm;
    border-radius: 50%;
    border: 3px solid $navy;
    overflow: hidden;
    background: #e8ecf0;
    margin: -1.1cm auto 0 auto;
    box-shadow: 0 4px 14px rgba(3, 35, 85, 0.25);
    z-index: 2;
    position: relative;
    flex-shrink: 0;

    .photo-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .student-name {
    text-align: center;
    font-family: "Outfit", sans-serif;
    font-size: 11pt;
    font-weight: 800;
    color: $navy;
    margin: 10px 8px 4px 8px;
    line-height: 1.1;
  }

  .student-group {
    text-align: center;
    font-size: 7.5pt;
    font-weight: 600;
    color: $accent;
    letter-spacing: 0.4px;
    margin-bottom: 6px;
  }

  .info-divider {
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(3, 35, 85, 0.15),
      transparent
    );
    margin: 0 14px 8px 14px;
  }

  .info-section {
    padding: 5px 14px 20px 14px;
    display: flex;
    flex-direction: column;
    gap: 11px;
    flex: 1;

    .info-row {
      display: flex;
      align-items: center;
      font-size: 7.5pt;

      .info-icon {
        color: $navy;
        margin-right: 5px;
        flex-shrink: 0;
      }
      .info-label {
        width: 45px;
        color: #555;
        font-weight: 600;
        flex-shrink: 0;
      }
      .info-value {
        color: #111;
        font-weight: 700;
        flex: 1;
        word-break: break-word;
      }
    }
  }

  .barcode-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 10px 0 4px 0;
    gap: 3px;

    .gr-code {
      font-size: 7pt;
      font-weight: 700;
      color: $navy;
      letter-spacing: 1px;
    }
  }

  .card-footer-bar {
    background: $navy;
    color: rgba(255, 255, 255, 0.85);
    font-size: 6pt;
    font-weight: 600;
    text-align: center;
    padding: 5px;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }
}

/* ─── BACK ─── */
.back {
  padding: 0;

  .back-header-bar {
    background: $navy;
    padding: 12px 14px 30px 14px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    .back-logo {
      height: 50px;
      object-fit: contain;
    }

    .back-curve-bottom {
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 22px;
      background: white;
      border-radius: 50% 50% 0 0 / 100% 100% 0 0;
    }
  }

  .terms-section {
    padding: 8px 14px 8px 14px;
    position: relative;
    z-index: 1;
    text-align: center;

    .terms-title {
      font-size: 8pt;
      font-weight: 800;
      color: $navy;
      margin: 0 0 4px 0;
    }
    .terms-text {
      font-size: 6.5pt;
      color: #777;
      line-height: 1.5;
      margin: 0;
    }
  }

  .divider-dashed {
    border-top: 1px dashed rgba(3, 35, 85, 0.2);
    margin: 6px 14px;
  }

  .dates-section-single {
    padding: 0 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 7.2pt;
    white-space: nowrap;

    .date-label {
      font-weight: 700;
      color: $navy;
    }
    .date-val {
      font-weight: 800;
      color: #111;
    }
    .date-sep {
      color: rgba(3, 35, 85, 0.3);
      font-weight: 400;
    }
  }

  .back-qr-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 15px;
    margin-bottom: 35px;
    position: relative;
    z-index: 1;

    .qr-gr-text {
      font-size: 8pt;
      font-weight: 700;
      color: $navy;
      margin-top: 4px;
      letter-spacing: 1px;
    }
  }

  .back-footer-sig {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: auto;
    margin-bottom: 28px;
    position: relative;
    z-index: 1;

    .sig-line {
      width: 110px;
      border-top: 1px solid $navy;
      margin-bottom: 4px;
    }
    .sig-label {
      font-size: 7pt;
      font-weight: 700;
      color: $navy;
    }
  }
}

.state-wrapper {
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

/* ─── PRINT ─── */
@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  @page {
    size: A4 portrait;
    margin: 8mm;
  }

  .happiness-id-card-report {
    padding: 0 !important;
    background: white !important;
  }

  .cards-grid {
    gap: 4mm !important;
    justify-content: flex-start !important;
    max-width: 100% !important;
  }

  .card-pair {
    gap: 3mm !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .id-card {
    width: 5.6cm !important;
    height: 9.2cm !important;
    box-shadow: none !important;
    border: 0.5px solid #ccc !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .card-header-curve,
  .card-footer-bar,
  .corner-tl,
  .corner-br,
  .photo-ring {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
